import {
  chatAccentPreset,
  readChatAccent,
  type ChatAccentId,
} from "@/lib/theme/chatAccent";

export type ChatTheme = "light" | "dark";

export const CHAT_THEME_STORAGE_KEY = "chat_theme";

const CHAT_PWA_THEME_COLOR: Record<ChatTheme, string> = {
  light: "#f8faff",
  dark: "#0f172a",
};

export function readChatTheme(): ChatTheme {
  try {
    const stored = localStorage.getItem(CHAT_THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* ignore */
  }
  return "light";
}

export function persistChatTheme(theme: ChatTheme): void {
  try {
    localStorage.setItem(CHAT_THEME_STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

function applyChatAccentVars(theme: ChatTheme, accentId: ChatAccentId): void {
  if (typeof document === "undefined") return;
  const preset = chatAccentPreset(accentId);
  const root = document.documentElement;
  root.dataset.chatAccent = preset.id;

  const style = root.style;
  style.setProperty("--chat-accent", preset.accent);
  style.setProperty("--chat-accent-rgb", preset.accentRgb);
  style.setProperty("--chat-accent-strong", preset.accentStrong);
  style.setProperty(
    "--chat-accent-soft",
    theme === "dark"
      ? `rgb(${preset.accentRgb} / 0.18)`
      : `color-mix(in srgb, ${preset.accent} 14%, #ffffff)`
  );
  style.setProperty(
    "--chat-bubble-mine",
    theme === "dark"
      ? `color-mix(in srgb, ${preset.accent} 42%, #1e293b)`
      : `color-mix(in srgb, ${preset.accent} 28%, #ffffff)`
  );
}

export function applyChatTheme(theme: ChatTheme, accent: ChatAccentId = readChatAccent()): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  applyChatAccentVars(theme, accent);

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta instanceof HTMLMetaElement) {
    meta.content = CHAT_PWA_THEME_COLOR[theme];
  }
}

export function applyChatAccent(accentId: ChatAccentId, theme: ChatTheme = readChatTheme()): void {
  applyChatTheme(theme, accentId);
}

export function toggleChatTheme(current: ChatTheme): ChatTheme {
  return current === "dark" ? "light" : "dark";
}
